"use client";

// ──────────────────────────────────────────────────────────────────────────
//  MarkRollDialog — fast path for "I just bonus-rolled and got X."
//
//  Modal with a single search input and a flat list of (pool · item).
//  Click a row → toggles state.exclusions[poolID][itemID]. Already-rolled
//  items render as struck-through with a check; clicking unmarks.
//
//  Stays open after a click so the user can chain marks (e.g. several
//  rolls in one raid night). ESC / backdrop click / Done button closes.
// ──────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ItemIcon } from "@/components/icons/ItemIcon";
import { useLootCache } from "@/lib/loot-cache";
import { useCharState, toggleExcluded } from "@/lib/state";
import { qualityTextClass, SLOT_LABEL } from "@/lib/wow";
import { isItemEligibleForClass } from "@/lib/classes";
import { cn } from "@/lib/utils";
import type { Item, Pool } from "@/lib/types";

interface Row {
  pool: Pool;
  item: Item;
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function MarkRollDialog({ open, onClose }: Props) {
  const cache = useLootCache();
  const state = useCharState();
  const [query, setQuery] = useState("");

  // Close on ESC.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset search when reopening.
  useEffect(() => { if (open) setQuery(""); }, [open]);

  const allRows: Row[] = useMemo(() => {
    const out: Row[] = [];
    for (const pool of Object.values(cache.pools)) {
      for (const itemID of pool.itemOrder) {
        const item = cache.items[itemID];
        if (!item) continue;
        // No point letting the user mark items their class can't equip.
        if (!isItemEligibleForClass(item.eligibleSpecs, state.classID)) continue;
        out.push({ pool, item });
      }
    }
    out.sort((a, b) => {
      // Pool label, then item name — gives "Boss A · Cloak", "Boss A · Helm" pairs.
      const pa = (a.pool.label || "").localeCompare(b.pool.label || "");
      if (pa !== 0) return pa;
      return a.item.name.localeCompare(b.item.name);
    });
    return out;
  }, [cache, state.classID]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(({ pool, item }) => {
      return (
        item.name.toLowerCase().includes(q) ||
        (item.bossName ?? "").toLowerCase().includes(q) ||
        pool.label.toLowerCase().includes(q) ||
        pool.instanceName.toLowerCase().includes(q)
      );
    });
  }, [allRows, query]);

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        className={cn(
          "fixed z-50 left-1/2 top-[8vh] -translate-x-1/2",
          "w-[720px] max-w-[95vw] max-h-[84vh]",
          "rounded-md border border-border-strong bg-surface",
          "shadow-[0_24px_60px_-20px_rgba(0,0,0,0.9)]",
          "flex flex-col",
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mark-roll-title"
      >
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <p className="eyebrow">Bonus roll</p>
            <p id="mark-roll-title" className="font-display text-base text-fg mt-0.5">
              Mark an item as rolled
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            Done
          </Button>
        </div>

        <div className="px-5 py-3 border-b border-border">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by item name, boss, or dungeon..."
            autoFocus
          />
          <p className="mt-2 text-[11px] text-fg-subtle">
            Click an item to mark it rolled. Click again to undo.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 && (
            <p className="px-5 py-10 text-center text-sm text-fg-subtle">
              No items match.
            </p>
          )}
          {filtered.map(({ pool, item }) => {
            const excluded = !!state.exclusions[pool.id]?.[item.id];
            return (
              <button
                key={`${pool.id}-${item.id}`}
                type="button"
                onClick={() => toggleExcluded(pool.id, item.id)}
                className={cn(
                  "w-full flex items-center gap-3 px-5 py-2 text-left",
                  "border-l-2 transition-colors",
                  excluded
                    ? "bg-surface-2/60 border-l-success"
                    : "border-l-transparent hover:bg-surface-2/40",
                )}
              >
                <ItemIcon
                  iconUrl={item.iconUrl}
                  iconName={item.iconName}
                  quality={item.quality}
                  size={28}
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      "block text-sm truncate",
                      qualityTextClass(item.quality),
                      excluded && "line-through opacity-70",
                    )}
                  >
                    {item.name}
                  </span>
                  <span className="block text-[11px] text-fg-subtle font-mono uppercase tracking-wider truncate">
                    {pool.label} · {SLOT_LABEL[item.slot] ?? item.slot}
                    {item.isToken ? " · Tier" : ""}
                  </span>
                </span>
                {excluded ? (
                  <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-success shrink-0">
                    <Check className="h-3.5 w-3.5" /> Rolled
                  </span>
                ) : (
                  <span className="text-[11px] font-mono uppercase tracking-wider text-fg-subtle shrink-0">
                    Mark rolled
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="px-5 py-3 border-t border-border flex items-center justify-between bg-surface-2/40">
          <p className="text-[11px] text-fg-subtle font-mono uppercase tracking-wider">
            {Object.values(state.exclusions).reduce((s, row) => s + Object.keys(row).length, 0)}{" "}
            items rolled overall
          </p>
          <button
            type="button"
            onClick={onClose}
            className="text-fg-subtle hover:text-fg transition-colors"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}
